import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getUserRole, getRoleById, hasPermission, PERMISSIONS } from '../utils/roleManager';

const { FiUser, FiShield, FiStar, FiClock, FiSettings } = FiIcons;

const UserProfile = ({ username }) => {
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    if (username) {
      const roleId = getUserRole(username);
      const role = getRoleById(roleId);
      setUserRole(role);
      
      if (role) {
        setUserPermissions(role.permissions);
      }
    }
  }, [username]);

  const getRoleColor = (roleId) => {
    const colors = {
      super_admin: 'from-red-500 to-red-700',
      admin: 'from-purple-500 to-purple-700',
      moderator: 'from-blue-500 to-blue-700',
      premium_user: 'from-yellow-500 to-yellow-700',
      user: 'from-green-500 to-green-700',
      guest: 'from-gray-500 to-gray-700'
    };
    return colors[roleId] || colors.user;
  };

  const formatPermissions = (permissions) => {
    if (permissions.includes('*')) {
      return ['All System Permissions'];
    }
    
    return permissions.map(permission => 
      permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  if (!userRole) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <div className="text-center text-gray-400">
          Loading user profile...
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
    >
      {/* Profile Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-16 h-16 bg-gradient-to-br ${getRoleColor(userRole.id)} rounded-full flex items-center justify-center`}>
          <SafeIcon icon={FiUser} className="text-2xl text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">{username}</h2>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiShield} className="text-blue-400" />
            <span className="text-blue-400 font-semibold">{userRole.name}</span>
            <span className="text-gray-400 text-sm">Level {userRole.level}</span>
          </div>
        </div>
      </div>

      {/* Role Description */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">Role Description</h3>
        <p className="text-gray-300">{userRole.description}</p>
      </div>

      {/* Permissions */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <SafeIcon icon={FiStar} className="text-yellow-400" />
          Permissions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {formatPermissions(userPermissions).map((permission, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 bg-white/5 rounded-lg"
            >
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-300 text-sm">{permission}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      {hasPermission(username, PERMISSIONS.VIEW_PERSONAL_STATS) && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <SafeIcon icon={FiSettings} className="text-gray-400" />
            Quick Actions
          </h3>
          <div className="flex flex-wrap gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              View Stats
            </motion.button>
            {hasPermission(username, PERMISSIONS.EXPORT_PERSONAL_DATA) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Export Data
              </motion.button>
            )}
            {hasPermission(username, PERMISSIONS.CUSTOM_THEMES) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                Themes
              </motion.button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default UserProfile;